import React, { useEffect, useState } from "react";
import InfoIcon from "@mui/icons-material/Info";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import Stack from "@mui/material/Stack";
import { PhotoProvider, PhotoView } from "react-photo-view";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  Tooltip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import CloseIcon from "@mui/icons-material/Close";

import Box from "@mui/material/Box";
import { changeOrderStatus } from "../features/ordersSlice";
import OrderActionButton from "./OrderActionButton";
import { timeAgo } from "../utils/timeAgo";

import "./../assets/css/OrdersTable.css";

export default function OrdersTable({
  title,
  orders,
  showDetail = false,
  highlightedId = null,
}) {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);

  const [selectedNotes, setSelectedNotes] = useState([]);
  const [openNotesDialog, setOpenNotesDialog] = useState(false);

  const handleOpenNotesDialog = (order) => {
    setSelectedNotes(order.note || []);
    setOpenNotesDialog(true);
  };

  const handleCloseNotesDialog = () => {
    setOpenNotesDialog(false);
    setSelectedNotes([]);
  };

  const handleOpenImages = (images) => {
    setSelectedImages(images);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImages([]);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedOrder(null);
  };

  const labelMap = {
    pending_dispatch: "غير مستلم بعد",
    claimed: "تم استلام الطلب",
    assigned: "تم تعين مزود",
    pending_user_payment: "تم تأكيد دفع العميل",
    scheduled: "تم جدولة الطلب",
    in_progress: "قيد التنفيذ",
    completed: "تم الانتهاء من المزود بنجاح",
    provider_payment_confirmation_pending: "تأكيد دفع المزود",
    awaiting_review: "تم الانتهاء من مراجعة العميل",
    closed: "مغلق",
    cancelled: "ملغى",
    failed: "فشل",
    refunded: "مسترجع",
  };

  const colorMap = {
    pending_dispatch: "#9e9e9e",
    claimed: "#0288d1",
    assigned: "#2196f3",
    pending_user_payment: "#fbc02d",
    scheduled: "#f57c00",
    in_progress: "#1976d2",
    completed: "#2e7d32",
    provider_payment_confirmation_pending: "#ffb300",
    awaiting_review: "#0288d1",
    closed: "#616161",
    cancelled: "#d32f2f",
    failed: "#c62828",
    refunded: "#6a1b9a",
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          mt: 4,
          boxShadow: 3,
          borderRadius: 2,
          overflowX: "auto",
        }}
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            p: 2,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}
        >
          <Typography variant="h5" align="center" fontWeight="bold">
            {title}
          </Typography>
        </Box>

        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow
              sx={(theme) => ({
                backgroundColor:
                  theme.palette.mode === "light"
                    ? theme.palette.grey[100]
                    : theme.palette.grey[900],
              })}
            >
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                رقم الطلب
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                التاريخ والوقت
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                الخدمة
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                الموقع
              </TableCell>

              {showDetail && (
                <>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    اسم الزبون
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    رقم هاتف الزبون
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    السعر
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    الملاحظات
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    اسم المزود
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    رقم هاتف المزود
                  </TableCell>

                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    حالة الطلب
                  </TableCell>

                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    الصور
                  </TableCell>
                </>
              )}

              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                الإجراءات
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {orders.map((order, index) => (
              <TableRow
                key={order.id}
                className={highlightedId === order.id ? "flash-row" : ""}
                sx={(theme) => ({
                  backgroundColor:
                    index % 2 === 0
                      ? theme.palette.action.hover
                      : theme.palette.background.paper,
                  "&:hover": {
                    backgroundColor: theme.palette.action.selected,
                  },
                  transition: "background-color 0.3s ease",
                })}
              >
                <TableCell align="center">{order.id}</TableCell>
                <TableCell align="center">
                  <Tooltip
                    title={new Date(order.created_at).toLocaleString("ar-EG", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  >
                    <span>{timeAgo(order.created_at)}</span>
                  </Tooltip>
                </TableCell>

                <TableCell align="center">{order.service.name}</TableCell>
                <TableCell align="center">{order.location.name}</TableCell>

                {showDetail && (
                  <>
                    <TableCell align="center">{order.name}</TableCell>
                    <TableCell align="center">{order.phone}</TableCell>
                    <TableCell align="center">{order.price}</TableCell>
                    <TableCell align="center">
                      {order.note?.length > 0 ? (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleOpenNotesDialog(order)}
                          startIcon={<ChatBubbleOutlineIcon />}
                          sx={{
                            borderRadius: "20px",
                            textTransform: "none",
                            fontWeight: 500,
                            px: 2,
                          }}
                        >
                          الملاحظات ({order.note.length})
                        </Button>
                      ) : (
                        <Chip
                          label="—"
                          size="small"
                          color="default"
                          variant="outlined"
                          sx={{ opacity: 0.6 }}
                        />
                      )}
                    </TableCell>

                    <TableCell align="center">
                      {order.provider ? (
                        order.provider.user.name
                      ) : (
                        <Chip label="لم يتم التعيين" color="warning" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {order.provider ? (
                        order.provider.user.phone
                      ) : (
                        <Chip label="لم يتم التعيين" color="warning" />
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        label={labelMap[order.status]}
                        sx={{
                          backgroundColor: colorMap[order.status] || "#9e9e9e",
                          color: "#fff",
                          fontWeight: "bold",
                        }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      {order.images && order.images.length > 0 ? (
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => handleOpenImages(order.images)}
                        >
                          عرض الصور
                        </Button>
                      ) : (
                        <Chip
                          label="لا توجد صور"
                          size="small"
                          color="default"
                        />
                      )}
                    </TableCell>
                  </>
                )}

                <TableCell align="center">
                  <OrderActionButton
                    order={order}
                    onAction={(order, updated) => {
                      dispatch(
                        changeOrderStatus({
                          orderId: order.id,
                          updates: updated,
                        })
                      );
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Popup for images */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            width: "100%",
            maxWidth: "900px",
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ position: "relative", p: 0, bgcolor: "#000" }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 10,
              color: "#fff",
            }}
          >
            <CloseIcon />
          </IconButton>

          <PhotoProvider>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
              }}
            >
              {selectedImages.map((img) => (
                <PhotoView
                  key={img.id}
                  src={`${process.env.REACT_APP_API_BASE_URL}${img.image}`}
                >
                  <img
                    src={`${process.env.REACT_APP_API_BASE_URL}${img.image}`}
                    alt={`order-${img.id}`}
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "80vh",
                      objectFit: "contain",
                      borderRadius: "8px",
                      cursor: "zoom-in",
                      marginBottom: "10px",
                    }}
                  />
                </PhotoView>
              ))}
            </Box>
          </PhotoProvider>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        dir="rtl"
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon color="primary" />
            <Typography variant="h6" color="primary">
              تفاصيل الطلب
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>اسم العميل</TableCell>
                  <TableCell>{selectedOrder.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>رقم الهاتف</TableCell>
                  <TableCell>{selectedOrder.phone}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>العنوان</TableCell>
                  <TableCell>{selectedOrder.address}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>العناصر</TableCell>
                  <TableCell>{selectedOrder.items}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>الحالة</TableCell>
                  <TableCell>{selectedOrder.status}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>الموظف المعالج</TableCell>
                  <TableCell>
                    {selectedOrder.dispatcher
                      ? selectedOrder.dispatcher
                      : "لم يتم التعيين بعد"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>التاريخ والوقت</TableCell>
                  <TableCell>{selectedOrder.date}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={openNotesDialog}
        onClose={handleCloseNotesDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            direction: "rtl",
            textAlign: "right",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center" }}>الملاحظات</DialogTitle>

        <DialogContent dividers sx={{ direction: "rtl" }}>
          {selectedNotes.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                maxHeight: 400,
                overflowY: "auto",
              }}
            >
              {selectedNotes.map((n, i) => (
                <Paper
                  key={i}
                  elevation={1}
                  sx={{
                    p: 2,
                    bgcolor: (theme) =>
                      theme.palette.mode === "light"
                        ? theme.palette.grey[50]
                        : theme.palette.grey[900],
                    borderRadius: 2,
                    border: (theme) =>
                      `1px solid ${
                        theme.palette.mode === "light"
                          ? theme.palette.grey[300]
                          : theme.palette.grey[800]
                      }`,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    <strong>{n.from}:</strong> {n.note}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {new Date(n.at).toLocaleString("ar-EG", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Paper>
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary" align="center">
              لا توجد ملاحظات لعرضها
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end" }}>
          <Button onClick={handleCloseNotesDialog} color="primary">
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
